import React, { useContext } from 'react';
import { Link } from 'react-router-dom';

import {
  IssueTypesDonut,
  SeverityLegend,
  SeverityTrendChart,
} from '@totem/components/services/threats/eventsDashboard/report/charts';
import EventsReportContext from '@totem/components/services/threats/eventsDashboard/report/EventsReportContext';
import {
  AgePill,
  SeverityPill,
} from '@totem/components/services/threats/eventsDashboard/report/pills';
import {
  formatDuration,
  getSeverity,
} from '@totem/components/services/threats/eventsDashboard/report/severity';
import {
  DashboardBuilding,
  FindingRow,
} from '@totem/components/services/threats/eventsDashboard/report/types';

import './eventsReport.css';

const TOP_BUILDINGS = 5;

export const buildingStatus = (building: DashboardBuilding) => {
  if (building.critical > 0) {
    return { label: 'Critical', style: 'status area-red' };
  }
  if (building.major + building.minor > 0) {
    return { label: 'Watch', style: 'status area-amber' };
  }
  return { label: 'Healthy', style: 'status area-green' };
};

// Critical and major issues first, then events in the period, then events processed.
export const bySeverity = (left: DashboardBuilding, right: DashboardBuilding) =>
  right.critical + right.major - (left.critical + left.major) ||
  right.total - left.total ||
  right.processed - left.processed ||
  left.name.localeCompare(right.name);

export const percentOf = (part: number, whole: number) => {
  if (whole <= 0) {
    return '—';
  }
  const percent = (part / whole) * 100;
  return `${percent > 0 && percent < 0.1 ? '<0.1' : percent.toFixed(1)}%`;
};

// The factors the urgency score weighs, in order: severity, how long the issue has been open, how often it repeats.
const priorityReasons = (finding: FindingRow) => {
  const days = Math.floor(finding.ageDays);
  const reasons = [
    getSeverity(finding.severity).label.toLowerCase(),
    days < 1 ? 'opened today' : `open ${days} day${days === 1 ? '' : 's'}`,
  ];
  if (finding.occurrenceCount > 1) {
    reasons.push(`repeated ${finding.occurrenceCount.toLocaleString()} times`);
  }
  return reasons.join(', ');
};

const Kpi = ({
  label,
  value,
  caption,
  color,
}: {
  label: string;
  value: React.ReactNode;
  caption: string;
  color?: string;
}) => (
  <div styleName="kpi">
    <div styleName="kpi-label">{label}</div>
    <div styleName="kpi-value" style={color ? { color } : undefined}>
      {value}
    </div>
    <div styleName="kpi-caption">{caption}</div>
  </div>
);

const SummaryTab = () => {
  const { summary, mode, setActiveTab, showCategory } =
    useContext(EventsReportContext);

  if (!summary) {
    return null;
  }

  const { totals, processed } = summary;
  const isResolved = mode === 'resolved';
  const attentionTotal = totals.critical + totals.major;
  const topBuildings = summary.buildings
    .filter((building) => building.total > 0)
    .sort(bySeverity)
    .slice(0, TOP_BUILDINGS);
  const activeAreas = summary.categories
    .filter((category) => category.findings > 0)
    .sort(
      (left, right) =>
        right.critical + right.major - (left.critical + left.major) ||
        right.findings - left.findings ||
        left.name.localeCompare(right.name),
    );
  const clearAreas = summary.categories.filter(
    (category) => category.findings === 0,
  );
  const groupAttention = (group: 'operations' | 'security') =>
    (summary.groupTotals[group]?.critical ?? 0) +
    (summary.groupTotals[group]?.major ?? 0);

  const processedKpi = (
    <Kpi
      label="Events processed"
      value={processed.events.toLocaleString()}
      caption="last 30 days, all severities"
    />
  );

  return (
    <div>
      <div styleName="kpi-strip">
        {isResolved ? (
          <>
            {processedKpi}
            <Kpi
              label="Resolved"
              value={totals.findings}
              caption="Minor and above"
              color="#22c55e"
            />
            <Kpi
              label="Critical · resolved"
              value={totals.critical}
              caption="highest level"
            />
            <Kpi
              label="Major · resolved"
              value={totals.major}
              caption="next level"
            />
            <Kpi
              label="Minor · resolved"
              value={totals.minor}
              caption="lower level"
            />
            <Kpi
              label="Average time to resolve"
              value={
                totals.findings > 0
                  ? formatDuration(totals.averageResolveHours)
                  : '—'
              }
              caption="opened to resolved"
            />
            <Kpi
              label="With a ticket"
              value={`${totals.withTicket} of ${totals.findings}`}
              caption="tracked by IB support"
            />
          </>
        ) : (
          <>
            {processedKpi}
            <Kpi
              label="Open issues"
              value={totals.findings}
              caption={`${percentOf(totals.findings, processed.events)} of processed`}
              color={totals.findings > 0 ? '#f97316' : '#22c55e'}
            />
            <Kpi
              label="Critical · open"
              value={totals.critical}
              caption="highest level"
              color={totals.critical > 0 ? '#ef4444' : undefined}
            />
            <Kpi
              label="Major · open"
              value={totals.major}
              caption="next level"
              color={totals.major > 0 ? '#f97316' : undefined}
            />
            <Kpi
              label="Minor · open"
              value={totals.minor}
              caption="lower level"
              color={totals.minor > 0 ? '#f59e0b' : undefined}
            />
            <Kpi
              label="With a ticket"
              value={`${totals.withTicket} of ${totals.findings}`}
              caption="tracked by IB support"
              color={
                totals.withTicket < totals.findings ? '#f59e0b' : undefined
              }
            />
            <Kpi
              label="Resolved"
              value={summary.resolvedRecent}
              caption="last 30 days"
              color="#22c55e"
            />
          </>
        )}
      </div>

      <div styleName="grid-callouts">
        {isResolved || summary.needsAttention.length === 0 ? (
          <div styleName="card callout callout-clear">
            <div styleName="card-body">
              {isResolved ? (
                'Open issues are not listed in the Resolved view.'
              ) : (
                <>
                  <b>All clear.</b> No critical or major issues are open.
                </>
              )}
            </div>
          </div>
        ) : (
          <div styleName="card callout">
            <div styleName="card-head">
              <span styleName="card-title">
                Needs attention · {attentionTotal}
              </span>
              <span styleName="card-sub">
                ranked by severity, then time open and repeats
              </span>
            </div>
            <div styleName="card-body callout-list">
              <div styleName="attention-row attention-head">
                <span>Severity</span>
                <span>Where</span>
                <span styleName="num">Open</span>
                <span>Ticket</span>
              </div>
              {summary.needsAttention.map((finding) => (
                <Link
                  key={finding.id}
                  to={`/dashboard/events2/${finding.id}`}
                  styleName="attention-row callout-row"
                >
                  <SeverityPill severity={finding.severity} />
                  <div styleName="attention-where">
                    <div styleName="row-title">
                      {finding.building.name || 'Unassigned building'}
                    </div>
                    <div styleName="row-sub">
                      {finding.eventType}
                      {finding.description
                        ? ` · ${finding.description}`
                        : finding.device.name && ` · ${finding.device.name}`}
                    </div>
                    <div styleName="why">Why: {priorityReasons(finding)}</div>
                  </div>
                  <AgePill days={finding.ageDays} />
                  {finding.ticket ? (
                    <span>{finding.ticket.number}</span>
                  ) : (
                    <span styleName="no-ticket">No ticket</span>
                  )}
                </Link>
              ))}
            </div>
            <div styleName="card-foot">
              Showing {summary.needsAttention.length} of {attentionTotal}{' '}
              critical and major issues. View all:{' '}
              <button
                type="button"
                styleName="link-button"
                onClick={() => setActiveTab('operations')}
              >
                Operations ({groupAttention('operations')}) ›
              </button>{' '}
              <button
                type="button"
                styleName="link-button"
                onClick={() => setActiveTab('security')}
              >
                Security ({groupAttention('security')}) ›
              </button>
            </div>
          </div>
        )}

        <div styleName="card">
          <div styleName="card-head">
            <span styleName="card-title">Service areas</span>
            <span styleName="card-sub">
              {isResolved ? 'resolved issues' : 'open issues'} · most urgent
              first · click to filter
            </span>
          </div>
          <div styleName="card-body">
            {activeAreas.length > 0 && (
              <div styleName="area-table">
                <div styleName="area-row area-head">
                  <span>Area</span>
                  <span styleName="num">
                    {isResolved ? 'Resolved' : 'Open'}
                  </span>
                </div>
                {activeAreas.map((category) => {
                  const urgent = [
                    category.critical > 0 && `${category.critical} critical`,
                    category.major > 0 && `${category.major} major`,
                  ].filter(Boolean);
                  return (
                    <button
                      key={category.key}
                      type="button"
                      styleName="area-row"
                      title={category.description}
                      onClick={() => showCategory(category.key)}
                    >
                      <span>
                        <span styleName="row-title">{category.name}</span>
                        {urgent.length > 0 && (
                          <span styleName="area-urgent">
                            {urgent.join(' · ')}
                          </span>
                        )}
                      </span>
                      <span styleName="num">{category.findings}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {clearAreas.length > 0 && (
              <div styleName="area-clear">
                <span styleName="status area-green">
                  {clearAreas.length}{' '}
                  {clearAreas.length === 1 ? 'area' : 'areas'}{' '}
                  {isResolved ? 'with none' : 'all clear'}
                </span>{' '}
                {clearAreas.map((category) => category.name).join(', ')}
              </div>
            )}
            <div styleName="meta">
              Minor and above. Routine activity is listed under &quot;Also
              noted&quot; on the Operations tab.
            </div>
          </div>
        </div>
      </div>

      <div styleName="grid-charts">
        <div styleName="card">
          <div styleName="card-head">
            <span styleName="card-title">Severity trend</span>
            <span styleName="card-sub">
              {isResolved
                ? 'Resolved events by day resolved'
                : 'Open events by day opened'}
            </span>
          </div>
          <div styleName="card-body">
            <SeverityTrendChart days={summary.trend} />
            <SeverityLegend />
          </div>
        </div>
        <div styleName="card">
          <div styleName="card-head">
            <span styleName="card-title">Event types</span>
            <span styleName="card-sub">
              {totals.events.toLocaleString()}{' '}
              {isResolved ? 'resolved' : 'open'} events, all severities
            </span>
          </div>
          <div styleName="card-body">
            <IssueTypesDonut types={summary.types} total={totals.events} />
          </div>
        </div>
      </div>

      <div styleName="card">
        <div styleName="card-head">
          <span styleName="card-title">
            Buildings · top {topBuildings.length} by severity
          </span>
          <button
            type="button"
            styleName="chip"
            onClick={() => setActiveTab('buildings')}
          >
            All buildings ›
          </button>
        </div>
        <div styleName="card-body">
          {topBuildings.length === 0 ? (
            <div styleName="empty">
              No buildings have events in this period.
            </div>
          ) : (
            <>
              <div styleName="building-row building-head">
                <span />
                <span>Building</span>
                <span styleName="num">Critical + major</span>
                <span styleName="num">
                  {isResolved ? 'Resolved events' : 'Open events'}
                </span>
                <span>Status</span>
              </div>
              {topBuildings.map((building, idx) => {
                const status = buildingStatus(building);
                const urgent = building.critical + building.major;
                return (
                  <div key={building.id} styleName="building-row">
                    <span styleName="rank">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <Link to={`/dashboard/buildings/${building.id}`}>
                        {building.name}
                      </Link>
                      <div styleName="sub">{building.region}</div>
                    </div>
                    <span styleName={urgent > 0 ? 'num' : 'num faint'}>
                      {urgent}
                    </span>
                    <span styleName="num">
                      {building.total.toLocaleString()}
                    </span>
                    <span>
                      <span styleName={status.style}>{status.label}</span>
                    </span>
                  </div>
                );
              })}
            </>
          )}
          <div styleName="meta">
            Ranked by critical and major issues first, then{' '}
            {isResolved ? 'resolved' : 'open'} events.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
